import { number, z, ZodSchema, type ZodErrorMap } from 'zod';
import type { IGameState, IStage } from '.';

export const BoardPanels = ['the-case', 'filter-facts', 'challenges', 'building-blocks'] as const;
export type BoardPanel = (typeof BoardPanels)[number];

export const FlikkenDataSchema = z.object({}).default({});

export type Stage = IStage | 'choose-theme' | 'play-rounds' | 'play-endgame';

export const ThemeSchema = z.object({
	title: z.string(),
	description: z.string(),
});

// Derived type from theme schema
export type Theme = z.infer<typeof ThemeSchema>;

export const FlikkenUpdateSchema = z.discriminatedUnion('kind', [
	z.object({ kind: z.literal('show-login-screen'), failed: z.boolean().optional() }),
	z.object({ kind: z.literal('show-register-screen') }),
	z.object({ kind: z.literal('show-wait-screen'), msg: z.string().optional(), playerName: z.string().optional() }),

	z.object({
		kind: z.literal('show-choosetheme-screen'),
		timerFrom: z.number(),
		timerTo: z.number(),
	}),
	z.object({
		kind: z.literal('show-roundtheme-screen'),
		director: z.string().optional(),
		theme: ThemeSchema,
		timerFrom: z.number(),
		timerTo: z.number(),
	}),
	z.object({ kind: z.literal('show-choosearchive-screen'), available: z.array(z.number()) }),
	z.object({
		kind: z.literal('show-choosequestion-screen'),
		numberOptions: z.array(z.number().nullable()),
		archive: z.enum(BoardPanels),
	}),
	z.object({ kind: z.literal('show-presentquestion-screen'), archive: z.string(), question: z.string() }),
	z.object({ kind: z.literal('show-reviewquestion-screen'), archive: z.string() }),

	z.object({
		kind: z.literal('show-choosegame-screen'),
		timerFrom: z.number(),
		timerTo: z.number(),
	}),
	z.object({
		kind: z.literal('show-presentgame-screen'),
		game: z.object({ title: z.string(), description: z.string() }),
		timerFrom: z.number(),
		timerTo: z.number(),
	}),
	z.object({ kind: z.literal('show-reviewgames-screen'), games: z.array(z.string()) }),
	z.object({
		kind: z.literal('show-results-screen'),
		players: z.array(z.object({ name: z.string(), color: z.string(), score: z.number() })),
	}),

	z.object({
		kind: z.literal('update-players-state'),
		players: z.array(z.object({ name: z.string(), color: z.string() })),
	}),
	z.object({
		kind: z.literal('update-player-state'),
		player: z.object({
			//
			name: z.string(),
			color: z.string(),
			score: z.number(),
			notes: z.string().optional(),
		}),
	}),

	z.object({ kind: z.literal('trigger-effect'), effect: z.enum(['confetti', 'red-flash']), text: z.string().optional() }),
]);
export type FlikkenUpdate = z.infer<typeof FlikkenUpdateSchema>;

export const FlikkenPlayerStateSchema = z.object({
	id: z.string(),
	active: z.boolean(),
	name: z.string(),
	color: z.string(),
	score: z.number(),
	lastScreen: FlikkenUpdateSchema,
	theme: ThemeSchema,
	// indexed as gameIdeas[playerIndex]
	gameIdeas: z.array(z.object({ title: z.string(), description: z.string(), rating: z.number() }).nullable()),
	notes: z.string().optional(),
});

// Derived type from schema
export type FlikkenPlayerState = z.infer<typeof FlikkenPlayerStateSchema>;

export const FlikkenClientPlayerStateSchema = z.object({
	name: z.string(),
	color: z.string(),
	score: z.number(),
	notes: z.string().optional(),
});
export type FlikkenClientPlayerState = z.infer<typeof FlikkenClientPlayerStateSchema>;

export type FlikkenState = {
	stage: Stage;
	roundIndex: number;
	lastRound: boolean;

	currentGameArchive?: BoardPanel;

	players: FlikkenPlayerState[];
	currentPlayerIndex: number;
	directorIndex: number;
	prevDirectors: number[];
	takenQuestions: Record<BoardPanel, number[]>;
	notes: string[];
};

export const FlikkenAdminUpdateSchema = z.discriminatedUnion('kind', [
	//
	z.object({ kind: z.literal('update-state'), state: z.any() }),
]);
export type FlikkenAdminUpdate = z.infer<typeof FlikkenAdminUpdateSchema>;

export const FlikkenMessageSchema = z.discriminatedUnion('kind', [
	// TODO: make it so this can only be sent by admin
	z.object({ kind: z.literal('start-game') }),
	z.object({ kind: z.literal('force-restart-round') }),
	z.object({ kind: z.literal('set-is-last-round'), lastRound: z.boolean() }),
	z.object({ kind: z.literal('set-player-active'), playerId: z.string(), active: z.boolean() }),
	z.object({ kind: z.literal('next-screen') }),

	// TODO: make it so this can be sent by any player
	z.object({ kind: z.literal('update-player-data'), playerId: z.string(), name: z.string(), color: z.string() }),
	z.object({ kind: z.literal('remove-player-data'), playerId: z.string() }),
	z.object({ kind: z.literal('choose-theme'), playerId: z.string(), theme: ThemeSchema }),
	z.object({
		kind: z.literal('choose-archive'),
		playerId: z.string(),
		archive: z.enum(BoardPanels),
	}),
	z.object({
		kind: z.literal('choose-question-number'),
		playerId: z.string(),
		num: z.number(),
	}),
	z.object({
		kind: z.literal('review-question'),
		playerId: z.string(),
		accepted: z.boolean(),
	}),
	z.object({
		kind: z.literal('choose-game'),
		playerId: z.string(),
		game: z.object({ title: z.string(), description: z.string() }),
	}),
	z.object({
		kind: z.literal('review-games'),
		playerId: z.string(),
		reviewedGames: z.array(z.number()),
	}),
	z.object({ kind: z.literal('save-notes'), playerId: z.string(), notes: z.string() }),
]);
export type FlikkenMessage = z.infer<typeof FlikkenMessageSchema>;
